import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Logo from "../assets/logo.png";
import { registerOnsite, searchAdmitCard } from "../api/auth";
import { useModal } from "../context/ModalContext";

const AVAILABLE_COURSES = [
  { name: "Artificial Intelligence (Generative AI & Agentic AI)" },
  { name: "Full Stack Digital Marketing & E-Commerce" },
  { name: "Full Stack Web Development" },
  { name: "Ethical Hacking" },
];

const OnsiteRegister = () => {
  const { showError } = useModal();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Admit Card Search Modal state
  const [showAdmitCardModal, setShowAdmitCardModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    fatherName: "",
    email: "",
    cnic: "",
    mobile: "",
    dateOfBirth: "",
    gender: "",
    qualification: "",
    courseApplied: "",
    city: "",
    address: "",
    password: "",
    agreement: false,
  });

  const [photo, setPhoto] = useState(null);
  const [errors, setErrors] = useState({});

  const clearError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.fatherName.trim())
      newErrors.fatherName = "Father's name is required";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Valid email is required";
    if (!formData.cnic.trim() || !/^\d{13}$/.test(formData.cnic.trim()))
      newErrors.cnic = "CNIC must be exactly 13 digits (without dashes)";
    if (!formData.mobile.trim() || !/^03\d{9}$/.test(formData.mobile))
      newErrors.mobile = "Valid mobile number is required (03xxxxxxxxx)";
    if (!formData.dateOfBirth)
      newErrors.dateOfBirth = "Date of birth is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.qualification)
      newErrors.qualification = "Qualification is required";
    if (!formData.courseApplied)
      newErrors.courseApplied = "Course selection is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.password || formData.password.length < 8)
      newErrors.password = "Password must be at least 8 characters long";
    if (!formData.agreement)
      newErrors.agreement = "You must agree to the terms and conditions";

    if (!photo) newErrors.photo = "Passport size photo is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let { width, height } = img;

          // Max dimensions for tiny photo ~ 120x120 to hit < 5KB target
          const MAX_SIZE = 120;
          if (width > height && width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          } else if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          // Compress aggressively to hit < 5KB target
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const newFile = new File(
                  [blob],
                  file.name.replace(/\.[^/.]+$/, ".jpg"),
                  {
                    type: "image/jpeg",
                    lastModified: Date.now(),
                  }
                );
                resolve(newFile);
              } else {
                reject(new Error("Canvas to Blob failed"));
              }
            },
            "image/jpeg",
            0.3
          );
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({ ...prev, photo: "File must be JPG or PNG" }));
        return;
      }
      try {
        const compressedFile = await compressImage(file);
        if (compressedFile.size > 5 * 1024) {
          setErrors((prev) => ({
            ...prev,
            photo: "Compressed file is still over 5KB. Try a smaller image.",
          }));
          setPhoto(null);
        } else {
          setPhoto(compressedFile);
          clearError("photo");
        }
      } catch (err) {
        setErrors((prev) => ({ ...prev, photo: "Failed to process image" }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    clearError(name);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      const response = await searchAdmitCard(searchQuery.trim());
      setSearchResults(response.data?.success ? response.data.data : []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError("Please fix the errors in the form before submitting.");
      return;
    }

    setLoading(true);

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });
      formDataToSubmit.append("photo", photo);

      const response = await registerOnsite(formDataToSubmit);
      if (response.data?.success) {
        const { challanId } = response.data.data;
        // Redirect to challan page immediately after successful registration
        navigate(`/onsite/challan/${challanId}`);
      } else {
        showError(response.data?.message || "Registration failed.");
      }
    } catch (error) {
      console.error("Onsite Registration failed:", error);
      showError(
        error.response?.data?.message || "Server error. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register mb-5">
      <div className="container" style={{ maxWidth: "800px" }}>
        <form
          onSubmit={handleSubmit}
          className="card p-4 shadow-sm border-0 mt-4"
        >
          <div className="text-center mb-4" style={{ position: "relative" }}>
            {/* Admit Card Button - top right */}
            {/* Admit Card button — temporarily hidden */}
            <img style={{ width: "200px" }} src={Logo} alt="logo" />
            <h2 className="fs-4 fw-bold mt-3">Onsite Registration Form</h2>
            <p className="text-danger fw-bold mb-0">
              Note: Onsite classes are only for students from Lahore and nearby areas.
            </p>
            <p className="text-muted">
              Before submitting this Admission Form, please ensure that all
              personal, educational, and contact information is accurate and
              complete. All information provided is subject to verification by
              Hunarmand Punjab. Any false, incorrect, or incomplete information
              may result in the rejection or cancellation of your application.{" "}
            </p>

          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control p-3 ${errors.fullName ? "is-invalid" : ""
                  }`}
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Full Name"
              />
              {errors.fullName && (
                <div className="invalid-feedback">{errors.fullName}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Father's Name <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control p-3 ${errors.fatherName ? "is-invalid" : ""
                  }`}
                type="text"
                name="fatherName"
                value={formData.fatherName}
                onChange={handleChange}
                placeholder="Father's Name"
              />
              {errors.fatherName && (
                <div className="invalid-feedback">{errors.fatherName}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control p-3 ${errors.email ? "is-invalid" : ""
                  }`}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Student Email"
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                CNIC <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control p-3 ${errors.cnic ? "is-invalid" : ""
                  }`}
                type="text"
                name="cnic"
                value={formData.cnic}
                onChange={handleChange}
                placeholder="CNIC (e.g. 3740512345678)"
                maxLength="13"
              />
              {errors.cnic && (
                <div className="invalid-feedback">{errors.cnic}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Mobile Number <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control p-3 ${errors.mobile ? "is-invalid" : ""
                  }`}
                type="text"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="03001234567"
                maxLength="11"
              />
              {errors.mobile && (
                <div className="invalid-feedback">{errors.mobile}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Date of Birth <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control p-3 ${errors.dateOfBirth ? "is-invalid" : ""
                  }`}
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                max={new Date().toISOString().split("T")[0]}
              />
              {errors.dateOfBirth && (
                <div className="invalid-feedback">{errors.dateOfBirth}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Gender <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select p-3 ${errors.gender ? "is-invalid" : ""
                  }`}
                name="gender"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && (
                <div className="invalid-feedback">{errors.gender}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Qualification <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select p-3 ${errors.qualification ? "is-invalid" : ""
                  }`}
                name="qualification"
                value={formData.qualification}
                onChange={handleChange}
              >
                <option value="">Select Qualification</option>
                <option value="matric">Matric</option>
                <option value="intermediate">Intermediate</option>
                <option value="bachelor">Bachelor / Higher</option>
              </select>
              {errors.qualification && (
                <div className="invalid-feedback">{errors.qualification}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Course Applied For <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select p-3 ${errors.courseApplied ? "is-invalid" : ""
                  }`}
                name="courseApplied"
                value={formData.courseApplied}
                onChange={handleChange}
              >
                <option value="">Choose Course</option>
                {AVAILABLE_COURSES.map((course) => (
                  <option key={course.name} value={course.name}>
                    {course.name}
                  </option>
                ))}
              </select>
              {errors.courseApplied && (
                <div className="invalid-feedback">{errors.courseApplied}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                City <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select p-3 ${errors.city ? "is-invalid" : ""}`}
                name="city"
                value={formData.city}
                onChange={handleChange}
              >
                <option value="">Select City</option>
                <option value="Lahore (Near by Areas)">Lahore (Near by Areas)</option>
              </select>
              {errors.city && (
                <div className="invalid-feedback">{errors.city}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Address <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control p-3 ${errors.address ? "is-invalid" : ""
                  }`}
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Complete Address"
              />
              {errors.address && (
                <div className="invalid-feedback">{errors.address}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="mb-2">
                Password <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control p-3 ${errors.password ? "is-invalid" : ""}`}
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Choose a password (min 8 characters)"
              />
              {errors.password && (
                <div className="invalid-feedback">{errors.password}</div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="mb-2">
              Passport Size Photo (Max 5KB){" "}
              <span className="text-danger">*</span>
            </label>

            <input
              type="file"
              className={`form-control p-3 ${errors.photo ? "is-invalid" : ""}`}
              accept="image/jpeg, image/png, image/jpg"
              onChange={handleFileChange}
            />
            {photo && (
              <div className="text-success mt-2">
                File selected and compressed successfully (
                {(photo.size / 1024).toFixed(2)} KB)
              </div>
            )}
            {errors.photo && (
              <div className="invalid-feedback d-block">{errors.photo}</div>
            )}
          </div>

          {/* <p className="text-muted mt-3" style={{ fontSize: "14px" }}>
            Before submitting this Admission Form, please ensure that all
            personal, educational, and contact information is accurate and
            complete. All information provided is subject to verification by
            Hunarmand Punjab. Any false, incorrect, or incomplete information
            may result in the rejection or cancellation of your application.
          </p> */}

          <div className="mb-3">
            <div className="form-check">
              <input
                className={`form-check-input ${errors.agreement ? "is-invalid" : ""
                  }`}
                type="checkbox"
                name="agreement"
                checked={formData.agreement || false}
                onChange={handleChange}
                id="agreement"
                required
              />
              <label
                className="form-check-label"
                htmlFor="agreement"
                style={{ fontSize: "14px" }}
              >
                I declare that all the information provided is correct to the
                best of my knowledge, and I agree to the{" "}
                <Link
                  to="/terms&policy"
                  className="text-success fw-bold text-decoration-underline"
                >
                  terms and conditions
                </Link>{" "}
                of the Hunarmand program. <span className="text-danger">*</span>
              </label>
              {errors.agreement && (
                <div className="invalid-feedback d-block">
                  {errors.agreement}
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-success fw-bold p-3 w-100"
            disabled={loading}
          >
            {loading ? "Registering..." : "Submit Registration"}
          </button>
        </form>
      </div>

      {/* ─── Admit Card Search Modal ─── */}
      {showAdmitCardModal && (
        <div
          className="modal d-block"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            inset: 0,
            zIndex: 1050,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAdmitCardModal(false);
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: "520px" }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">🪪 Find Admit Card</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAdmitCardModal(false)}
                />
              </div>
              <div className="modal-body">
                <form onSubmit={handleSearch} className="d-flex gap-2 mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name or phone number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={searchLoading}
                  >
                    {searchLoading ? "..." : "Search"}
                  </button>
                </form>

                {/* Results */}
                {searchResults.length > 0 ? (
                  <ul className="list-group">
                    {searchResults.map((s) => (
                      <li
                        key={s._id}
                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <div className="fw-semibold">{s.fullName}</div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "12px" }}
                          >
                            {s.phone} — {s.courseApplied}
                          </div>
                          <div
                            className="text-success"
                            style={{ fontSize: "11px" }}
                          >
                            {s.admitCardId}
                          </div>
                        </div>
                        <a
                          href={`/onsite/admit-card/${s.admitCardId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-success"
                        >
                          Open
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : searchQuery && !searchLoading ? (
                  <div className="text-center text-muted py-3">
                    No results found for "{searchQuery}"
                  </div>
                ) : null}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAdmitCardModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnsiteRegister;
